import {
  WSActions,
  WSButton,
  WSCard,
  WSForm,
  WSInfoBox,
  WSInputMask,
  WSList,
  WSLoader,
  WSModalOld,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  MfaFactor,
  MfaFactorCreateType,
  MfaFactorStatus,
  OrganizationSsoConfigStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesMfa } from "@query";
import { useSession } from "@hooks/useSession";
import { OTPDisclosure } from "../../../../shared/components/OPTDisclosure";
import { validatorOTP } from "../../../../shared/validators/validatorOTP";
import {
  FormDataPhoneNumber,
  FormPartialPhoneNumber,
  validationSchemaPhoneNumber
} from "../../components/FormPartialPhoneNumber";

import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const SecureAccount: React.FC<FlowStep> = ({ onNext }) => {
  const session = useSession();
  const factorsQuery = queriesMfa.listPersonMfaFactors.useQuery({
    personId: session.personId
  });
  const hasActiveSmsFactor = (factorsQuery.data?.data ?? []).some(
    factor =>
      factor.type === MfaFactorCreateType.SMS &&
      factor.status === OrganizationSsoConfigStatus.ACTIVE
  );
  const history = useHistory();
  const createAcccountPath = history.location.pathname.replace("/secure", "");

  const [createFactor, createFactorMeta] =
    queriesMfa.createMfaFactor.useMutation();
  const [verifyFactor, verifyFactorMeta] =
    queriesMfa.verifyMfaFactor.useMutation();

  const [pendingFactor, setPendingFactor] = useState<{
    factor: MfaFactor;
    phoneNumber: string;
  } | null>(null);

  useEffect(() => {
    const skipPhoneVerification =
      sessionStorage.getItem("skipPhoneVerification") === "true";

    if (skipPhoneVerification || hasActiveSmsFactor) {
      // Using replace to prevent history break. This step is being skipped, so it should not be in the history stack
      history.replace(createAcccountPath + "/account-type");
    }
  }, [hasActiveSmsFactor, onNext, history, createAcccountPath]);

  if (factorsQuery.isLoading) {
    return <WSLoader.Spinner />;
  }

  return (
    <>
      {pendingFactor && (
        <WSModalOld
          title="Verify your phone number"
          size="S"
          onClose={() => {
            setPendingFactor(pendingFactor);
          }}
        >
          <WSForm<{
            otp: string;
          }>
            onSubmit={async data => {
              const result = await verifyFactor({
                mfaFactorId: pendingFactor.factor.id,
                mfaFactorVerifyRequest: {
                  smsCode: data.otp
                }
              });

              if (result?.status === MfaFactorStatus.ACTIVE) {
                onNext?.();
              }
            }}
            validationSchema={Yup.object().shape({
              otp: validatorOTP
            })}
          >
            <WSList gap="L">
              <WSInfoBox>
                Enter the 6-digit code sent to {pendingFactor.phoneNumber}
              </WSInfoBox>

              <WSForm.Field
                name="otp"
                component={WSInputMask}
                componentProps={{
                  mask: "999999"
                }}
              />

              <OTPDisclosure />

              <ErrorAlert error={verifyFactorMeta.error} context="MfaFactor" />

              <WSActions
                alignment="fill"
                orientation="vertical"
                buttons={[
                  {
                    kind: "Link",
                    label: "Resend code",
                    onClick: () => setPendingFactor(null),
                    type: "button"
                  },
                  {
                    label: "Continue",
                    loading: verifyFactorMeta.isLoading
                  }
                ]}
              />
            </WSList>
          </WSForm>
        </WSModalOld>
      )}

      <WSForm<{
        phone: FormDataPhoneNumber;
      }>
        onSubmit={async data => {
          const phoneNumber = `+${data.phone.countryCode}${data.phone.phoneNumber}`;

          const factor = await createFactor({
            mfaFactorCreate: {
              type: MfaFactorCreateType.SMS,
              label: phoneNumber,
              smsPhone: phoneNumber
            }
          });

          if (factor) {
            setPendingFactor({ factor, phoneNumber });
          }
        }}
        defaultValues={{
          phone: {
            countryCode: "1",
            phoneNumber: ""
          }
        }}
        validationSchema={Yup.object().shape({
          phone: validationSchemaPhoneNumber
        })}
      >
        <WSList gap="2XL">
          <WSCard header="Secure your account">
            <WSList gap="2XL">
              <WSInfoBox>
                A 6 digit verification code will be sent via SMS to your phone
                number. Message and data rates may apply.
              </WSInfoBox>

              <FormPartialPhoneNumber name="phone" label="Phone number" />

              <OTPDisclosure />

              <ErrorAlert error={createFactorMeta.error} context="MfaFactor" />
            </WSList>
          </WSCard>
          <WSButton size="M" fullWidth loading={createFactorMeta.isLoading}>
            Send code
          </WSButton>

          <WSText.ParagraphXs color="gray400">
            The information on this page is required to setup your Wingspan
            account, if you exit or leave without completing this step, you will
            return to this page the next time you sign-in
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </>
  );
};
